/**
 * CustomerFormInline - Formulário de edição inline para modal de cliente
 * 
 * Exibido dentro do modal premium substituindo o conteúdo de visualização
 * Permite editar o cliente sem abrir um dialog separado
 * 
 * @version 1.0.0
 * RELEASE: 2026-01-10
 */
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  User, 
  Building2, 
  MapPin, 
  Search,
  Loader2,
  Save,
  Calendar,
  ArrowLeft
} from 'lucide-react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SellerSelector } from '@/components/crm/SellerSelector';
import { 
  useCrmCustomers, 
  CrmCustomer, 
  UpdateCustomerInput
} from '@/hooks/useCrmCustomers';
import { applyCPFMask, applyCNPJMask, applyDateMask } from '@/lib/input-masks';
import { validateCPF, validateCNPJ } from '@/lib/input-validators';
import { toast } from 'sonner';

// ============================================
// FUNÇÕES DE CONVERSÃO DE DATA
// ============================================

/**
 * Converte data do formato brasileiro (DD/MM/YYYY) para ISO (YYYY-MM-DD)
 * Necessário porque o PostgreSQL espera formato ISO
 */
function convertBrazilianDateToISO(date: string | null | undefined): string | null {
  if (!date) return null;
  
  const cleanDate = date.replace(/\D/g, '');
  if (cleanDate.length !== 8) return null;
  
  const day = cleanDate.slice(0, 2);
  const month = cleanDate.slice(2, 4);
  const year = cleanDate.slice(4, 8);
  
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);
  
  if (dayNum < 1 || dayNum > 31) return null;
  if (monthNum < 1 || monthNum > 12) return null;
  if (yearNum < 1900 || yearNum > 2100) return null;
  
  return `${year}-${month}-${day}`;
}

/**
 * Converte data do formato ISO (YYYY-MM-DD) para brasileiro (DD/MM/YYYY)
 */
function convertISOToBrazilianDate(date: string | null | undefined): string | null {
  if (!date) return null;
  
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

// ============================================
// SCHEMA DE VALIDAÇÃO
// ============================================
const customerFormSchema = z.object({
  customer_type: z.enum(['pf', 'pj']),
  
  // Campos PF
  nome: z.string().optional().nullable(),
  cpf: z.string().optional().nullable(),
  data_nascimento: z.string().optional().nullable(),
  
  // Campos PJ
  razao_social: z.string().optional().nullable(),
  nome_fantasia: z.string().optional().nullable(),
  cnpj: z.string().optional().nullable(),
  
  // Endereço
  cep: z.string().optional().nullable(),
  logradouro: z.string().optional().nullable(),
  numero: z.string().optional().nullable(),
  complemento: z.string().optional().nullable(),
  bairro: z.string().optional().nullable(),
  cidade: z.string().optional().nullable(),
  estado: z.string().optional().nullable(),
  
  // Outros
  seller_id: z.string().optional().nullable(),
  // lifecycle_stage removido: cliente é sempre 'active'
  notes: z.string().optional().nullable(),
});

type CustomerFormValues = z.infer<typeof customerFormSchema>;

// ============================================
// PROPS
// ============================================
interface CustomerFormInlineProps {
  /** Cliente a ser editado */
  customer: CrmCustomer;
  /** Callback ao cancelar (volta para visualização) */
  onCancel: () => void;
  /** Callback ao salvar com sucesso */
  onSuccess: () => void;
}

// ============================================
// COMPONENTE
// ============================================
export function CustomerFormInline({
  customer,
  onCancel,
  onSuccess,
}: CustomerFormInlineProps) {
  const { updateCustomer, isUpdating } = useCrmCustomers();
  const [isLoadingCep, setIsLoadingCep] = useState(false);

  // Formulário com react-hook-form
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      customer_type: customer.customer_type,
    },
  });


  // Observa tipo de cliente para renderização condicional
  const customerType = form.watch('customer_type');

  // Preenche formulário com dados do cliente
  useEffect(() => {
    form.reset({
      customer_type: customer.customer_type,
      nome: customer.nome,
      cpf: customer.cpf,
      data_nascimento: convertISOToBrazilianDate(customer.data_nascimento),
      razao_social: customer.razao_social,
      nome_fantasia: customer.nome_fantasia,
      cnpj: customer.cnpj,
      cep: customer.cep,
      logradouro: customer.logradouro,
      numero: customer.numero,
      complemento: customer.complemento,
      bairro: customer.bairro,
      cidade: customer.cidade,
      estado: customer.estado,
      seller_id: customer.seller_id,
      
      notes: customer.notes,
    });
  }, [customer, form]);

  // ============================================
  // BUSCA CEP VIA VIACEP
  // ============================================
  const handleCepSearch = async () => {
    const cep = form.getValues('cep')?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
      toast.error('CEP inválido. Digite 8 dígitos.');
      return;
    }

    setIsLoadingCep(true);
    
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      
      if (data.erro) {
        toast.error('CEP não encontrado.');
        return;
      }
      
      form.setValue('logradouro', data.logradouro || '');
      form.setValue('bairro', data.bairro || '');
      form.setValue('cidade', data.localidade || '');
      form.setValue('estado', data.uf || '');
      
      toast.success('Endereço preenchido!');
    } catch (error) {
      toast.error('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setIsLoadingCep(false);
    }
  };

  // ============================================
  // SUBMIT
  // ============================================
  const onSubmit = async (values: CustomerFormValues) => {
    // Validação de CPF/CNPJ
    if (values.customer_type === 'pf' && values.cpf) {
      const cpfResult = validateCPF(values.cpf);
      if (!cpfResult.isValid) {
        form.setError('cpf', { message: cpfResult.error });
        return;
      }
    }
    
    if (values.customer_type === 'pj' && values.cnpj) {
      const cnpjResult = validateCNPJ(values.cnpj);
      if (!cnpjResult.isValid) {
        form.setError('cnpj', { message: cnpjResult.error });
        return;
      }
    }

    const input: UpdateCustomerInput = {
      id: customer.id,
      customer_type: values.customer_type,
      nome: values.nome,
      cpf: values.cpf,
      data_nascimento: convertBrazilianDateToISO(values.data_nascimento),
      razao_social: values.razao_social,
      nome_fantasia: values.nome_fantasia,
      cnpj: values.cnpj,
      cep: values.cep,
      logradouro: values.logradouro,
      numero: values.numero,
      complemento: values.complemento,
      bairro: values.bairro,
      cidade: values.cidade,
      estado: values.estado,
      seller_id: values.seller_id,
      // Cliente é sempre ativo (status removido da UI)
      lifecycle_stage: 'active',
      notes: values.notes,
    };

    updateCustomer.mutate(input, {
      onSuccess: () => {
        onSuccess();
      },
    });
  };

  // ============================================
  // MÁSCARAS
  // ============================================
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCPFMask(e.target.value);
    form.setValue('cpf', masked);
  };

  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCNPJMask(e.target.value);
    form.setValue('cnpj', masked);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    form.setValue('data_nascimento', masked);
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const limited = value.slice(0, 8);
    let formatted = limited;
    if (limited.length > 5) {
      formatted = `${limited.slice(0, 5)}-${limited.slice(5, 8)}`;
    }
    form.setValue('cep', formatted);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header do modo edição */}
      <div className="flex items-center justify-between gap-3 px-4 h-12 border-b bg-background shrink-0">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onCancel}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            {customerType === 'pf' ? (
              <User className="h-4 w-4 text-blue-600" />
            ) : (
              <Building2 className="h-4 w-4 text-violet-600" />
            )}
            <h2 className="text-sm font-semibold">Editar Cliente</h2>
          </div>
        </div>
      </div>

      {/* Formulário scrollável */}
      <ScrollArea className="flex-1">
        <Form {...form}>
          <form 
            id="customer-edit-form"
            onSubmit={form.handleSubmit(onSubmit)} 
            className="p-6 space-y-6 max-w-3xl mx-auto"
          >
            {/* Tipo de Cliente */}
            <FormField
              control={form.control}
              name="customer_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Cliente</FormLabel>
                  <Tabs
                    value={field.value}
                    onValueChange={field.onChange}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="pf" className="gap-2">
                        <User className="h-4 w-4" />
                        Pessoa Física
                      </TabsTrigger>
                      <TabsTrigger value="pj" className="gap-2">
                        <Building2 className="h-4 w-4" />
                        Pessoa Jurídica
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </FormItem>
              )}
            />

            <Separator />

            {/* Campos Pessoa Física */}
            {customerType === 'pf' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Dados Pessoais
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="nome"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Nome Completo</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nome completo" 
                            {...field} 
                            value={field.value || ''} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cpf"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CPF</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="000.000.000-00" 
                            {...field}
                            value={field.value || ''}
                            onChange={handleCpfChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="data_nascimento"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Data de Nascimento</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input 
                              placeholder="DD/MM/AAAA" 
                              {...field}
                              value={field.value || ''}
                              onChange={handleDateChange}
                            />
                            <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {/* Campos Pessoa Jurídica */}
            {customerType === 'pj' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Dados da Empresa
                </h4>
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="razao_social"
                    render={({ field }) => (
                      <FormItem className="sm:col-span-2">
                        <FormLabel>Razão Social</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Razão social da empresa" 
                            {...field} 
                            value={field.value || ''} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="nome_fantasia"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome Fantasia</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Nome fantasia" 
                            {...field}
                            value={field.value || ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="cnpj"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>CNPJ</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="00.000.000/0000-00" 
                            {...field}
                            value={field.value || ''}
                            onChange={handleCnpjChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            <Separator />

            {/* Endereço */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Endereço
              </h4>
              <div className="grid gap-4 sm:grid-cols-6">
                <FormField
                  control={form.control}
                  name="cep"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>CEP</FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <Input 
                            placeholder="00000-000" 
                            {...field}
                            value={field.value || ''}
                            onChange={handleCepChange}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handleCepSearch}
                            disabled={isLoadingCep}
                          >
                            {isLoadingCep ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Search className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="logradouro"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-4">
                      <FormLabel>Logradouro</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Rua, Avenida..." 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="numero"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-1">
                      <FormLabel>Número</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Nº" 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="complemento"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Complemento</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Apto, Sala..." 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bairro"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-3">
                      <FormLabel>Bairro</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Bairro" 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cidade"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-4">
                      <FormLabel>Cidade</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Cidade" 
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="estado"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>UF</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="UF" 
                          maxLength={2}
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Vendedor responsável */}
            <div className="grid gap-4 sm:grid-cols-2">

              <FormField
                control={form.control}
                name="seller_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vendedor Responsável</FormLabel>
                    <FormControl>
                      <SellerSelector
                        value={field.value || undefined}
                        onSelect={(sellerId) => field.onChange(sellerId || null)}
                        placeholder="Selecione um vendedor"
                        allowClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Campo "Status" removido: cliente é sempre Ativo */}

            </div>

            {/* Notas */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Observações sobre o cliente..."
                      className="resize-none"
                      rows={3}
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </ScrollArea>

      {/* Footer fixo com ações */}
      <div className="flex items-center justify-end gap-2 px-4 h-14 border-t bg-background shrink-0">
        <Button 
          variant="outline" 
          onClick={onCancel} 
          disabled={isUpdating}
        >
          Cancelar
        </Button>
        <Button 
          type="submit"
          form="customer-edit-form"
          disabled={isUpdating}
        >
          {isUpdating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Salvar Alterações
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
